import {Field, ErrorMessage, useField} from "formik";
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import {InputHTMLAttributes, useRef, useState} from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import type {ComponentProps} from "react";
import {useFormikContext} from "formik";

type ReactDatePickerProps = ComponentProps<typeof DatePicker>;
interface FormsInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  name: string;
  label?: string;
  placeholder?: string;
  size?: "default" | "sm";
  error?: string | null;
  pickup?: boolean;
  required?: boolean;
  note?: boolean;
}
type DateInputProps = {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
} & Pick<
  ReactDatePickerProps,
  | "minDate"
  | "maxDate"
  | "dateFormat"
  | "showTimeSelect"
  | "timeFormat"
  | "timeIntervals"
  | "showYearDropdown"
  | "scrollableYearDropdown"
  | "yearDropdownItemNumber"
>;
export const Input = ({
  name,
  label,
  placeholder,
  size = "default",
  error,
  required = true,
  ...props
}: FormsInputProps) => {
  const [field, meta] = useField(name);
  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;

  const sizeClasses = {
    default: "!h-6",
    sm: "!h-4",
  };

  return (
    <div className="!flex !flex-col !gap-1">
      {label && (
        <label htmlFor={name} className="!text-sm !text-gray-600">
          {label}
          {required && <span className="!text-red-500">*</span>}
        </label>
      )}
      <Field
        name={name}
        id={name}
        autoComplete="off"
        aria-autocomplete="none"
        placeholder={placeholder}
        onBlur={field.onBlur}
        className={`
			!rounded-lg !border !bg-white !px-4 !py-3 !text-sm !text-gray-900 !h-12
			focus:!outline-none ${sizeClasses[size]}
			${!meta.touched && "!border-gray-100"}
			${
        isActive
          ? `focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]`
          : ""
      }
			${
        hasError
          ? `!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]`
          : ""
      }
		  `}
        {...props}
      />
      <ErrorMessage name={name}>
        {(msg) => (
          <div className="!flex !items-center !gap-1 !mt-1">
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                stroke="#DF1C41"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p className="!text-xs !font-semibold !text-[#DF1C41]">{msg}</p>
          </div>
        )}
      </ErrorMessage>
      {/* Show custom error if provided */}
      {error && !meta.error && (
        <div className="!flex !items-center !gap-1 !mt-1">
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
              stroke="#DF1C41"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <p className="!text-xs !font-semibold !text-[#DF1C41]">{error}</p>
        </div>
      )}
    </div>
  );
};

export const Textarea = ({
  name,
  label,
  placeholder,
  error,
  required = true,
  note = false,
  ...props
}: FormsInputProps) => {
  const [field, meta] = useField(name);
  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;

  const sizeClasses = {
    default: "!min-h-[120px]",
    sm: "!min-h-[80px]",
  };

  return (
    <div className="!flex !flex-col !gap-1">
      {label && (
        <label htmlFor={name} className="!text-sm !text-gray-600">
          {label} {required && <span className="!text-red-500">*</span>}
        </label>
      )}
      {note && (
        <p className="text-xs bg-green-300/5 border border-green-600 text-green-600 rounded-2xl px-4 py-2 mb-1">
          <strong>Format: </strong> Item value, description, insurance request
          (interstate/international only), and delivery instructions.
          <br /> <strong>Example: </strong>₦25,000 – Sneakers – Yes Insurance –
          Apt 2B or ₦15,000 – Laptop Bag – No Insurance – Leave with reception
        </p>
      )}
      <Field
        as="textarea"
        name={name}
        id={name}
        autoComplete="off"
        aria-autocomplete="none"
        placeholder={placeholder}
        onBlur={field.onBlur}
        className={`
			!rounded-lg !h-[142px] !border !bg-white !px-4 !py-3 !text-sm !text-gray-900 !resize-none
			focus:!outline-none
			${!meta.touched && "!border-gray-100"}
			${
        isActive
          ? "focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]"
          : ""
      }
			${
        hasError
          ? "!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]"
          : ""
      }
		  `}
        {...props}
      />
      <ErrorMessage name={name}>
        {(msg) => (
          <div className="!flex !items-center !gap-1 !mt-1">
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                stroke="#DF1C41"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p className="!text-xs !font-semibold !text-[#DF1C41]">{msg}</p>
          </div>
        )}
      </ErrorMessage>
      {/* Show custom error if provided */}
      {error && !meta.error && (
        <div className="!flex !items-center !gap-1 !mt-1">
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
              stroke="#DF1C41"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <p className="!text-xs !font-semibold !text-[#DF1C41]">{error}</p>
        </div>
      )}
    </div>
  );
};

export const InputPassword: React.FC<FormsInputProps> = ({
  label,
  name,
  placeholder,
  size = "default",
  required = true,
  ...props
}) => {
  const [field, meta] = useField(name);
  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;

  const sizeClasses = {
    default: "!h-12",
    sm: "!h-10",
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="!w-full !flex !flex-col gap-2">
      <label htmlFor={name} className="!text-sm !text-gray-600">
        {label} {required && <span className="!text-red-500">*</span>}
      </label>
      <div>
        <div className="!flex !relative">
          <Field
            type={showPassword ? "text" : "password"}
            id={name}
            name={name}
            autoComplete="off"
            aria-autocomplete="none"
            placeholder={placeholder}
            onBlur={field.onBlur}
            className={`
				!w-full !rounded-lg !border !bg-white !px-4 !py-3 !text-sm !text-gray-900
				focus:!outline-none ${sizeClasses[size]}
				${!meta.touched && "!border-gray-100"}
				${
          isActive
            ? `focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]`
            : ""
        }
				${
          hasError
            ? `!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]`
            : ""
        }
			  `}
            {...props}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="!absolute !bottom-3 !right-3 !p-1 !z-10 !text-gray/60 !cursor-pointer"
          >
            {showPassword ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.75 10C1.75 10 4.75 4 10 4C15.25 4 18.25 10 18.25 10C18.25 10 15.25 16 10 16C4.75 16 1.75 10 1.75 10Z"
                  stroke="#818898"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 12.25C11.2426 12.25 12.25 11.2426 12.25 10C12.25 8.75736 11.2426 7.75 10 7.75C8.75736 7.75 7.75 8.75736 7.75 10C7.75 11.2426 8.75736 12.25 10 12.25Z"
                  stroke="#818898"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.75 10C1.75 10 4.75 4 10 4C15.25 4 18.25 10 18.25 10C18.25 10 15.25 16 10 16C4.75 16 1.75 10 1.75 10Z"
                  stroke="#818898"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10 12.25C11.2426 12.25 12.25 11.2426 12.25 10C12.25 8.75736 11.2426 7.75 10 7.75C8.75736 7.75 7.75 8.75736 7.75 10C7.75 11.2426 8.75736 12.25 10 12.25Z"
                  stroke="#818898"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            )}
          </button>
        </div>
        <ErrorMessage name={name}>
          {(msg) => (
            <div className="!flex !items-center !gap-1 !mt-1">
              <svg
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                  stroke="#DF1C41"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <p className="!text-xs !font-semibold !text-[#DF1C41]">{msg}</p>
            </div>
          )}
        </ErrorMessage>
      </div>
    </div>
  );
};

export const DateInput = ({
  name,
  label,
  placeholder = "Select Date",
  required = true,
  ...props
}: DateInputProps) => {
  const [field, meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<DatePicker>(null);

  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;

  const formattedDate = field.value
    ? moment(field.value).format("dddd, Do MMMM")
    : placeholder;

  const handleDateChange = (
    date: Date | null,
    event?: React.SyntheticEvent<any>
  ) => {
    if (date) {
      helpers.setValue(date.toISOString());
    } else {
      helpers.setValue("");
    }
    helpers.setTouched(true, true);
    setIsOpen(false);
  };

  return (
    <div className="!flex !flex-col !gap-1">
      {label && (
        <label htmlFor={name} className="!text-sm !text-gray-600">
          {label} {required && <span className="!text-red-500">*</span>}
        </label>
      )}

      <div
        className={`
			!w-full !h-12 !rounded-lg !border !bg-white !px-4 !py-3 !text-sm !text-gray-900
			focus:!outline-none !cursor-pointer
			${!meta.touched && "!border-gray-100"}
			${
        isActive
          ? `focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]`
          : ""
      }
			${
        hasError
          ? `!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]`
          : ""
      }
			relative
		  `}
        onClick={() => {
          setIsOpen(!isOpen);
          datePickerRef.current?.setFocus();
        }}
      >
        <div className="!flex !items-center !justify-between !h-full">
          <div className="!flex !items-center !gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.66699 1.66602V4.16602"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.333 1.66602V4.16602"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M2.91699 7.57422L17.0837 7.57422"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M17.5 7.08268V14.166C17.5 16.666 16.25 18.3327 13.3333 18.3327H6.66667C3.75 18.3327 2.5 16.666 2.5 14.166L2.5 7.08268C2.5 4.58268 3.75 2.91602 6.66667 2.91602L13.3333 2.91602C16.25 2.91602 17.5 4.58268 17.5 7.08268Z"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.0791 11.4167H13.0866"
                stroke="#666D80"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.0791 13.9167H13.0866"
                stroke="#666D80"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.99607 11.4167H10.0036"
                stroke="#666D80"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.99607 13.9167H10.0036"
                stroke="#666D80"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6.91209 11.4167H6.91957"
                stroke="#666D80"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6.91209 13.9167H6.91957"
                stroke="#666D80"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p
              className={`!text-sm ${
                !field.value ? "!text-gray-500" : "!text-gray-900"
              }`}
            >
              {formattedDate}
            </p>
          </div>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.5 6.75L9 11.25L13.5 6.75"
              stroke="black"
              stroke-width="1.125"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <DatePicker
          ref={datePickerRef}
          selected={field.value ? new Date(field.value) : new Date()}
          onChange={handleDateChange}
          open={isOpen}
          onCalendarClose={() => setIsOpen(false)}
          onClickOutside={() => setIsOpen(false)}
          calendarClassName="!border !border-gray-100 !rounded-lg !shadow-lg"
          wrapperClassName="w-full"
          className="absolute opacity-0"
          popperPlacement="bottom-start"
          showPopperArrow={false}
          {...props}
        />
      </div>

      <ErrorMessage name={name}>
        {(msg) => (
          <div className="!flex !items-center !gap-1 !mt-1">
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                stroke="#DF1C41"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p className="text-xs font-semibold text-[#DF1C41]">{msg}</p>
          </div>
        )}
      </ErrorMessage>
    </div>
  );
};

// Time Input Component
export const TimeInput = ({
  name,
  label,
  placeholder = "Select Time",
  required = true,
  ...props
}: FormsInputProps) => {
  const [field, meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const timeInputRef = useRef<any>(null);

  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;

  // Format the time for display (HH:MM AM/PM)
  const formattedTime = field.value
    ? new Date(`1970-01-01T${field.value}`).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : placeholder;

  const handleTimeClick = () => {
    setIsOpen(true);
    timeInputRef.current?.showPicker();
  };

  return (
    <div className="!flex !flex-col !gap-1">
      {label && (
        <label htmlFor={name} className="!text-sm !text-gray-600">
          {label} {required && <span className="!text-red-500">*</span>}
        </label>
      )}

      <div
        className={`
			!w-full !h-12 !rounded-lg !border !bg-white !px-4 !py-3 !text-sm !text-gray-900
			focus:!outline-none !cursor-pointer
			${!meta.touched && "!border-gray-100"}
			${
        isActive
          ? `focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]`
          : ""
      }
			${
        hasError
          ? `!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]`
          : ""
      }
			!relative
		  `}
        onClick={handleTimeClick}
      >
        {/* Hidden actual time input */}
        <Field
          name={name}
          innerRef={timeInputRef}
          type="time"
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            field.onBlur(e);
            setIsOpen(false);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            helpers.setValue(e.target.value);
          }}
          className="!absolute !inset-0 !opacity-0 !w-full !h-full !cursor-pointer"
          {...props}
        />

        {/* Custom time display */}
        <div className="!flex !items-center !justify-between !h-full">
          <div className="!flex !items-center !gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.3337 9.99935C18.3337 14.5993 14.6003 18.3327 10.0003 18.3327C5.40033 18.3327 1.66699 14.5993 1.66699 9.99935C1.66699 5.39935 5.40033 1.66602 10.0003 1.66602C14.6003 1.66602 18.3337 5.39935 18.3337 9.99935Z"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.0914 12.6495L10.5081 11.1078C10.0581 10.8411 9.69141 10.1995 9.69141 9.67448V6.25781"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p
              className={`!text-sm ${
                !field.value ? "!text-gray-500" : "!text-gray-900"
              }`}
            >
              {formattedTime}
            </p>
          </div>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.5 6.75L9 11.25L13.5 6.75"
              stroke="black"
              stroke-width="1.125"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>

      <ErrorMessage name={name}>
        {(msg) => (
          <div className="!flex !items-center !gap-1 !mt-1">
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                stroke="#DF1C41"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p className="!text-xs !font-semibold !text-[#DF1C41]">{msg}</p>
          </div>
        )}
      </ErrorMessage>
    </div>
  );
};

export const TimeSelect = ({
  name,
  label,
  options = [],
  placeholder = "Select Time",
  required = true,
  ...props
}: FormsInputProps & {options: {value: string; label: string}[]}) => {
  const [field, meta, helpers] = useField(name);
  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;

  const selectedOption =
    options.find((option) => option.value === field.value)?.label ||
    placeholder;

  return (
    <div className="!flex !flex-col !gap-1">
      {label && (
        <label htmlFor={name} className="!text-sm !text-gray-600">
          {label} {required && <span className="!text-red-500">*</span>}
        </label>
      )}
      <Menu as="div" className="relative">
        <MenuButton
          id={name}
          className={`
			  !h-12 !rounded-lg !border !bg-white !pl-10 !pr-4 !text-sm !text-left !text-gray-900
			  focus:!outline-none !flex !items-center !justify-between !relative
			  ${!meta.touched && "!border-gray-100"}
			  ${
          isActive
            ? `focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]`
            : ""
        }
			  ${
          hasError
            ? `!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]`
            : ""
        }
			`}
        >
          <div className="!absolute !left-3 !top-1/2 !transform !-translate-y-1/2 !text-gray-400">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.3337 9.99935C18.3337 14.5993 14.6003 18.3327 10.0003 18.3327C5.40033 18.3327 1.66699 14.5993 1.66699 9.99935C1.66699 5.39935 5.40033 1.66602 10.0003 1.66602C14.6003 1.66602 18.3337 5.39935 18.3337 9.99935Z"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.0914 12.6495L10.5081 11.1078C10.0581 10.8411 9.69141 10.1995 9.69141 9.67448V6.25781"
                stroke="#666D80"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <span className={!field.value ? "!text-gray-600" : ""}>
            {selectedOption}
          </span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.5 6.75L9 11.25L13.5 6.75"
              stroke="black"
              stroke-width="1.125"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </MenuButton>

        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <MenuItems className="!absolute !z-10 !mt-1 !w-full !max-h-60 !overflow-auto !rounded-md !bg-white !py-1 !shadow-lg !ring-1 !ring-black/5 focus:!outline-none">
            {options.map((option) => (
              <MenuItem key={option.value}>
                {({active}) => (
                  <button
                    type="button"
                    className={`${
                      active
                        ? "!bg-[#fe7f1f]/60 !text-[#fe7f1f]"
                        : "!text-gray-700"
                    } !group !flex !w-full !items-center !px-4 !py-2 !text-sm`}
                    onClick={() => {
                      helpers.setValue(option.value);
                      helpers.setTouched(true);
                    }}
                  >
                    {option.label}
                  </button>
                )}
              </MenuItem>
            ))}
          </MenuItems>
        </Transition>
      </Menu>
      <ErrorMessage name={name}>
        {(msg) => (
          <div className="!flex !items-center !gap-1 !mt-1">
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                stroke="#DF1C41"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p className="!text-xs !font-semibold !text-[#DF1C41]">{msg}</p>
          </div>
        )}
      </ErrorMessage>
    </div>
  );
};

// Select Dropdown Component
export const SelectInput = ({
  name,
  label,
  options = [],
  placeholder = "Select Option",
  error,
  required = true,
  ...props
}: FormsInputProps & {options: {value: string; label: string}[]}) => {
  const [field, meta, helpers] = useField(name);
  const isActive = meta.touched && !meta.error;
  const hasError = meta.touched && meta.error;
  const {validateForm} = useFormikContext();

  const selectedOption =
    options.find((option) => option.value === field.value)?.label ||
    placeholder;

  return (
    <div className="!flex !flex-col !gap-1">
      {label && (
        <div>
          <label htmlFor={name} className="!text-sm !text-gray-600">
            {label} {required && <span className="!text-red-500">*</span>}
          </label>
        </div>
      )}
      <Menu as="div" className="!relative">
        <MenuButton
          id={name}
          className={`
			!h-12 !rounded-lg !border !bg-white !px-4 !text-sm !text-left !text-gray-900
			  focus:!outline-none !flex !items-center !justify-between
			  ${!meta.touched && "!border-gray-100"}
			  ${
          isActive
            ? `focus:!border-[#FE7F20] focus:!shadow-[0px_0px_1px_3px_rgba(254,127,32,0.15)]`
            : ""
        }
			  ${
          hasError
            ? `!border-[#DF1C41] focus:!shadow-[0px_0px_1px_3px_rgba(223,28,65,0.15)]`
            : ""
        }
			`}
        >
          <span className={!field.value ? "!text-gray-600" : ""}>
            {selectedOption}
          </span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.5 6.75L9 11.25L13.5 6.75"
              stroke="black"
              stroke-width="1.125"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </MenuButton>

        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <MenuItems className="!absolute !z-10 !mt-1 !w-full !max-h-60 !overflow-auto !rounded-md !bg-white !p-4 !shadow-lg !ring-1 !ring-black/5 focus:!outline-none">
            {options.map((option) => (
              <MenuItem key={option.value}>
                {({active}) => (
                  <button
                    type="button"
                    className={`${
                      active
                        ? "!bg-[#fe7f1f]/60 !text-[#fe7f1f]"
                        : "!text-gray-400"
                    } !group !rounded-lg !flex !w-full !items-center !px-4 !py-2 !text-sm`}
                    onClick={() => {
                      helpers.setValue(option.value);
                      helpers.setTouched(true);
                      setTimeout(() => validateForm(), 0);
                    }}
                  >
                    {option.label}
                  </button>
                )}
              </MenuItem>
            ))}
          </MenuItems>
        </Transition>
      </Menu>
      <ErrorMessage name={name}>
        {(msg) => (
          <div className="!flex !items-center !gap-1 !mt-1">
            <svg
              width="14"
              height="15"
              viewBox="0 0 14 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
                stroke="#DF1C41"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <p className="text-xs font-semibold text-[#DF1C41]">{msg}</p>
          </div>
        )}
      </ErrorMessage>
      {/* Show custom error if provided */}
      {error && !meta.error && (
        <div className="!flex !items-center !gap-1 !mt-1">
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7 4.83333V7.5M7 10.1667H7.00667M1 7.5C1 8.28793 1.15519 9.06815 1.45672 9.7961C1.75825 10.5241 2.20021 11.1855 2.75736 11.7426C3.31451 12.2998 3.97595 12.7417 4.7039 13.0433C5.43185 13.3448 6.21207 13.5 7 13.5C7.78793 13.5 8.56815 13.3448 9.2961 13.0433C10.0241 12.7417 10.6855 12.2998 11.2426 11.7426C11.7998 11.1855 12.2417 10.5241 12.5433 9.7961C12.8448 9.06815 13 8.28793 13 7.5C13 5.9087 12.3679 4.38258 11.2426 3.25736C10.1174 2.13214 8.5913 1.5 7 1.5C5.4087 1.5 3.88258 2.13214 2.75736 3.25736C1.63214 4.38258 1 5.9087 1 7.5Z"
              stroke="#DF1C41"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <p className="!text-xs !font-semibold !text-[#DF1C41]">{error}</p>
        </div>
      )}
    </div>
  );
};
